// The direct-manipulation layer: transparent hit-areas for selection, plus a
// selection box with resize + rotate handles for the active layer. Rendered in
// DISPLAY pixels over the (scaled) canvas; converts pointer deltas back to the
// composition's normalized [0..1] space so edits map 1:1 to the master.

import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type PointerEvent as ReactPointerEvent,
} from 'react'
import { debounce } from 'lodash'
import type { CompositionEditor } from './use-composition-editor'
import type { Layer, TextLayer, TextRun } from '../types'
import { groupRunsIntoLines, resolveFontFamily } from '../render-helpers'
import { applyResize, clampNorm as clamp, type ResizeHandle } from './composition-ops'
import { serializeRuns } from './inline-text-serialize'
import { useTextRangeSelection } from './use-text-range-selection'

type Handle = ResizeHandle

const HANDLES: { id: Handle; cx: number; cy: number; cursor: string }[] = [
  { id: 'nw', cx: 0, cy: 0, cursor: 'nwse-resize' },
  { id: 'n', cx: 0.5, cy: 0, cursor: 'ns-resize' },
  { id: 'ne', cx: 1, cy: 0, cursor: 'nesw-resize' },
  { id: 'e', cx: 1, cy: 0.5, cursor: 'ew-resize' },
  { id: 'se', cx: 1, cy: 1, cursor: 'nwse-resize' },
  { id: 's', cx: 0.5, cy: 1, cursor: 'ns-resize' },
  { id: 'sw', cx: 0, cy: 1, cursor: 'nesw-resize' },
  { id: 'w', cx: 0, cy: 0.5, cursor: 'ew-resize' },
]

// A rotate cursor matching the OS cursor language: a black curved-arrow glyph with a thin white
// outline, sized like a native pointer (~18px, thin). Hotspot centered.
const ROTATE_ARROWS = '<path d="M21 12a9 9 0 1 1-2.64-6.36"/><path d="M21 3v6h-6"/>'
const ROTATE_SVG = `<svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke-linecap="round" stroke-linejoin="round"><g stroke="white" stroke-width="4">${ROTATE_ARROWS}</g><g stroke="black" stroke-width="2">${ROTATE_ARROWS}</g></svg>`
const ROTATE_CURSOR = `url("data:image/svg+xml,${encodeURIComponent(ROTATE_SVG)}") 9 9, grab`

type Box = { x: number; y: number; w: number; h: number }

type Gesture =
  | { kind: 'move'; startX: number; startY: number; boxX: number; boxY: number }
  | { kind: 'resize'; handle: Handle; box: Box; startX: number; startY: number }
  | { kind: 'rotate'; cx: number; cy: number; startAngle: number; startRotation: number }
  // Two-finger pinch on touch: scale (about center) + rotate the selected layer at once.
  | {
      kind: 'pinch'
      layerId: string
      startDist: number
      startAngle: number
      cx: number
      cy: number
      box: Box
      rotation: number
      fontSizePct?: number
    }

const SIZE_MIN = 0.02
const SIZE_MAX = 3
const FONT_MIN = 0.005
const FONT_MAX = 0.5
const clampSize = (v: number) => Math.max(SIZE_MIN, Math.min(SIZE_MAX, v))
const dist = (a: { x: number; y: number }, b: { x: number; y: number }) =>
  Math.hypot(a.x - b.x, a.y - b.y)
const angleDeg = (a: { x: number; y: number }, b: { x: number; y: number }) =>
  (Math.atan2(b.y - a.y, b.x - a.x) * 180) / Math.PI

export function SelectionOverlay({
  editor,
  scale,
  containerRef,
  touch = false,
  hitDisabledIds,
}: {
  editor: CompositionEditor
  scale: number
  containerRef: React.RefObject<HTMLDivElement | null>
  /** Touch mode (mobile): enlarged handle hit-areas, two-finger pinch scale+rotate, and no
   *  hover-highlight (there is no hover on touch). */
  touch?: boolean
  /** Video editor: layers NOT visible at the current playhead. Their hit-targets go
   *  pointer-events:none so an invisible clip can't steal clicks from the visible layer
   *  underneath (they stay selectable from the timeline/Layers panel). */
  hitDisabledIds?: Set<string>
}) {
  const { comp, selected, select, editingId } = editor
  const dims = { w: comp.canvas.width, h: comp.canvas.height }
  const gesture = useRef<Gesture | null>(null)
  // Active touch points (client coords) for pinch detection.
  const pointers = useRef<Map<number, { x: number; y: number }>>(new Map())
  // One begin/end-gesture session even as we switch move -> pinch mid-drag (one undo step).
  const gestureOpen = useRef(false)
  const [hoveredId, setHoveredId] = useState<string | null>(null)

  const openGesture = useCallback(() => {
    if (!gestureOpen.current) {
      editor.beginGesture()
      gestureOpen.current = true
    }
  }, [editor])
  const closeGesture = useCallback(() => {
    gesture.current = null
    if (gestureOpen.current) {
      editor.endGesture()
      gestureOpen.current = false
    }
  }, [editor])

  // Upgrade to a two-finger pinch on the selected layer (scale about center + rotate).
  const startPinch = useCallback(() => {
    const l = editor.selected
    if (!l || l.locked) return
    const pts = [...pointers.current.values()]
    if (pts.length < 2) return
    openGesture()
    gesture.current = {
      kind: 'pinch',
      layerId: l.id,
      startDist: dist(pts[0], pts[1]) || 1,
      startAngle: angleDeg(pts[0], pts[1]),
      cx: l.x + l.w / 2,
      cy: l.y + l.h / 2,
      box: { x: l.x, y: l.y, w: l.w, h: l.h },
      rotation: l.rotation ?? 0,
      fontSizePct: l.type === 'text' ? l.fontSizePct : undefined,
    }
  }, [editor, openGesture])

  // Pointer (client) → normalized [0..1] composition coords.
  const toNorm = useCallback(
    (clientX: number, clientY: number) => {
      const rect = containerRef.current?.getBoundingClientRect()
      if (!rect) return { x: 0, y: 0 }
      return { x: (clientX - rect.left) / scale / dims.w, y: (clientY - rect.top) / scale / dims.h }
    },
    [containerRef, scale, dims.w, dims.h]
  )

  useEffect(() => {
    const onMove = (e: PointerEvent) => {
      if (pointers.current.has(e.pointerId))
        pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY })
      const g = gesture.current
      if (!g) return
      if (g.kind === 'pinch') {
        const pts = [...pointers.current.values()]
        if (pts.length < 2) return
        const factor = dist(pts[0], pts[1]) / g.startDist
        const w = clampSize(g.box.w * factor)
        const h = clampSize(g.box.h * factor)
        const patch = {
          w,
          h,
          x: g.cx - w / 2,
          y: g.cy - h / 2,
          rotation: Math.round(g.rotation + (angleDeg(pts[0], pts[1]) - g.startAngle)),
          ...(g.fontSizePct != null
            ? { fontSizePct: Math.max(FONT_MIN, Math.min(FONT_MAX, g.fontSizePct * factor)) }
            : {}),
        } as Partial<TextLayer>
        editor.updateLayer(g.layerId, patch, { gesture: true })
        return
      }
      if (!selected) return
      const n = toNorm(e.clientX, e.clientY)
      if (g.kind === 'move') {
        editor.updateLayer(
          selected.id,
          { x: clamp(g.boxX + (n.x - g.startX)), y: clamp(g.boxY + (n.y - g.startY)) },
          { gesture: true }
        )
      } else if (g.kind === 'resize') {
        const next = applyResize(g.handle, g.box, n.x - g.startX, n.y - g.startY)
        editor.updateLayer(selected.id, next, { gesture: true })
      } else if (g.kind === 'rotate') {
        // Rotate RELATIVE to the angle the handle was grabbed at, added to the layer's rotation
        // at grab — so grabbing doesn't snap the layer to an absolute angle, and a pre-rotated
        // layer keeps its rotation and turns from there.
        const cur = (Math.atan2(n.y * dims.h - g.cy, n.x * dims.w - g.cx) * 180) / Math.PI
        editor.updateLayer(
          selected.id,
          { rotation: Math.round(g.startRotation + (cur - g.startAngle)) },
          { gesture: true }
        )
      }
    }
    const onUp = (e: PointerEvent) => {
      pointers.current.delete(e.pointerId)
      const g = gesture.current
      if (!g) return
      // Lifting ONE finger mid-pinch but keeping the other down: continue as a one-finger MOVE
      // with the survivor (Canva behaviour) instead of freezing the layer until every finger lifts.
      if (g.kind === 'pinch' && pointers.current.size === 1) {
        const l = editor.selected
        const [pt] = [...pointers.current.values()]
        if (l && pt) {
          const n = toNorm(pt.x, pt.y)
          gesture.current = { kind: 'move', startX: n.x, startY: n.y, boxX: l.x, boxY: l.y }
          return
        }
      }
      // Pinch needs two fingers; single-pointer gestures end when all up.
      if (g.kind === 'pinch' ? pointers.current.size < 2 : pointers.current.size === 0) {
        closeGesture()
      }
    }
    window.addEventListener('pointermove', onMove)
    window.addEventListener('pointerup', onUp)
    window.addEventListener('pointercancel', onUp)
    return () => {
      window.removeEventListener('pointermove', onMove)
      window.removeEventListener('pointerup', onUp)
      window.removeEventListener('pointercancel', onUp)
    }
  }, [editor, selected, toNorm, dims.h, dims.w, closeGesture])

  const startMove = (e: ReactPointerEvent, layer: Layer) => {
    e.stopPropagation()
    if (layer.locked) return
    pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY })
    // A second finger on the already-selected layer upgrades to a pinch (scale + rotate).
    if (touch && pointers.current.size >= 2 && editor.selected) {
      startPinch()
      return
    }
    select(layer.id)
    const n = toNorm(e.clientX, e.clientY)
    openGesture()
    gesture.current = { kind: 'move', startX: n.x, startY: n.y, boxX: layer.x, boxY: layer.y }
  }

  const startResize = (e: ReactPointerEvent, handle: Handle) => {
    e.stopPropagation()
    if (!selected) return
    pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY })
    const n = toNorm(e.clientX, e.clientY)
    openGesture()
    gesture.current = {
      kind: 'resize',
      handle,
      box: { x: selected.x, y: selected.y, w: selected.w, h: selected.h },
      startX: n.x,
      startY: n.y,
    }
  }

  const startRotate = (e: ReactPointerEvent) => {
    e.stopPropagation()
    if (!selected) return
    pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY })
    openGesture()
    const cx = (selected.x + selected.w / 2) * dims.w
    const cy = (selected.y + selected.h / 2) * dims.h
    const n = toNorm(e.clientX, e.clientY)
    gesture.current = {
      kind: 'rotate',
      cx,
      cy,
      // The pointer's angle at the moment of grab — the rotate baseline (delta from here is applied).
      startAngle: (Math.atan2(n.y * dims.h - cy, n.x * dims.w - cx) * 180) / Math.PI,
      startRotation: selected.rotation ?? 0,
    }
  }

  const hoveredLayer = hoveredId ? comp.layers.find(l => l.id === hoveredId) : null
  const editingLayer =
    editingId != null
      ? (comp.layers.find(l => l.id === editingId && l.type === 'text') as TextLayer | undefined)
      : undefined

  return (
    <div style={{ position: 'absolute', inset: 0 }}>
      {/* Background: tap empty space deselects; a second finger here during a selection starts
          a pinch on the selected layer. */}
      <div
        style={{ position: 'absolute', inset: 0, touchAction: touch ? 'none' : undefined }}
        onPointerDown={e => {
          pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY })
          if (touch && pointers.current.size >= 2 && editor.selected) {
            startPinch()
            return
          }
          if (pointers.current.size <= 1) select(null)
        }}
      />
      {[...comp.layers]
        .sort((a, b) => a.z - b.z)
        .filter(l => !l.hidden && !l.locked)
        .map(l => {
          // Seeded full-frame washes (grade/scrim) are decorative and span the whole
          // canvas; if they captured clicks, the background photo beneath them could
          // never be selected by clicking it (you'd always grab the topmost grade).
          // Let clicks pass through to the layer below — they stay editable from the
          // Layers panel.
          const decorative = !!l.seeded && (l.role === 'grade' || l.role === 'scrim')
          const hitDisabled = decorative || !!hitDisabledIds?.has(l.id)
          return (
            <div
              key={l.id}
              data-layer-id={l.id}
              data-layer-type={l.type}
              data-layer-role={l.role}
              onPointerDown={e => startMove(e, l)}
              onDoubleClick={() => l.type === 'text' && editor.beginTextEdit(l.id)}
              onPointerEnter={() => !touch && setHoveredId(l.id)}
              onPointerLeave={() => !touch && setHoveredId(id => (id === l.id ? null : id))}
              style={{
                position: 'absolute',
                left: l.x * dims.w * scale,
                top: l.y * dims.h * scale,
                width: l.w * dims.w * scale,
                height: l.h * dims.h * scale,
                transform: l.rotation ? `rotate(${l.rotation}deg)` : undefined,
                cursor: 'move',
                pointerEvents: hitDisabled ? 'none' : undefined,
                touchAction: touch ? 'none' : undefined,
              }}
            />
          )
        })}

      {/* Hover highlight — outlines the layer under the cursor (desktop only; no hover on touch,
          skipped for the selected layer which already shows its selection box). */}
      {!touch && hoveredLayer && hoveredLayer.id !== selected?.id && (
        <div
          data-layer-hover={hoveredLayer.id}
          style={{
            position: 'absolute',
            left: hoveredLayer.x * dims.w * scale,
            top: hoveredLayer.y * dims.h * scale,
            width: hoveredLayer.w * dims.w * scale,
            height: hoveredLayer.h * dims.h * scale,
            transform: hoveredLayer.rotation ? `rotate(${hoveredLayer.rotation}deg)` : undefined,
            border: '1.5px solid color-mix(in srgb, var(--acid, #c6ff3d) 65%, transparent)',
            borderRadius: 3,
            boxShadow: '0 0 0 1px rgba(0,0,0,0.12)',
            pointerEvents: 'none',
          }}
        />
      )}

      {/* Inline text editor — type directly on the canvas (the rendered layer is suppressed
          while editing; see CompositionCanvas.suppressLayerId). */}
      {editingLayer && (
        <InlineTextEditor
          key={editingLayer.id}
          layer={editingLayer}
          dims={dims}
          scale={scale}
          editor={editor}
          onDone={() => editor.endTextEdit()}
        />
      )}

      {/* Selection box + handles. */}
      {selected && !selected.locked && editingId !== selected.id && (
        <div
          data-selection-box={selected.id}
          style={{
            position: 'absolute',
            left: selected.x * dims.w * scale,
            top: selected.y * dims.h * scale,
            width: selected.w * dims.w * scale,
            height: selected.h * dims.h * scale,
            transform: selected.rotation ? `rotate(${selected.rotation}deg)` : undefined,
            border: '1.5px solid var(--acid, #c6ff3d)',
            boxShadow: '0 0 0 1px rgba(0,0,0,0.35)',
            pointerEvents: 'none',
          }}
        >
          {HANDLES.map(h => {
            // Touch gets a big invisible hit target around a small visible dot.
            const hit = touch ? 40 : 10
            const dot = touch ? 16 : 10
            return (
              <div
                key={h.id}
                onPointerDown={e => startResize(e, h.id)}
                style={{
                  position: 'absolute',
                  left: `calc(${h.cx * 100}% - ${hit / 2}px)`,
                  top: `calc(${h.cy * 100}% - ${hit / 2}px)`,
                  width: hit,
                  height: hit,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: h.cursor,
                  pointerEvents: 'auto',
                  touchAction: touch ? 'none' : undefined,
                }}
              >
                <div
                  style={{
                    width: dot,
                    height: dot,
                    background: 'var(--acid, #c6ff3d)',
                    border: '1px solid rgba(0,0,0,0.5)',
                    borderRadius: 2,
                  }}
                />
              </div>
            )
          })}
          {/* Rotate handle — sits OPPOSITE the floating menu (which anchors above when there's
              room, mirroring FloatingToolbar's threshold): menu above → handle below, and when
              the menu is forced below near the top edge the handle flips above. Avoids overlap. */}
          {(() => {
            const hit = touch ? 40 : 12
            const dot = touch ? 16 : 12
            const below = selected.y * dims.h * scale > 52
            const top = below ? 'calc(100% + 16px)' : touch ? -44 : -28
            return (
              <div
                onPointerDown={startRotate}
                style={{
                  position: 'absolute',
                  left: `calc(50% - ${hit / 2}px)`,
                  top,
                  width: hit,
                  height: hit,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: ROTATE_CURSOR,
                  pointerEvents: 'auto',
                  touchAction: touch ? 'none' : undefined,
                }}
              >
                <div
                  style={{
                    width: dot,
                    height: dot,
                    background: 'var(--bg, #08090b)',
                    border: '1.5px solid var(--acid, #c6ff3d)',
                    borderRadius: '50%',
                  }}
                />
              </div>
            )
          })()}
        </div>
      )}
    </div>
  )
}

function placeCaretEnd(el: HTMLElement) {
  const range = document.createRange()
  range.selectNodeContents(el)
  range.collapse(false)
  const sel = window.getSelection()
  sel?.removeAllRanges()
  sel?.addRange(range)
}

// --- rich inline editor DOM helpers (styled spans ↔ runs) ------------------
// The editor renders each run as a styled <span> grouped into line <div>s (WYSIWYG: the gold
// "PARIS" shows gold + big), so a user can SELECT a span and restyle just it. Typing is native;
// on input we serialize the DOM back to runs (style read from each span's data-*), preserving
// per-span emphasis. Re-render only happens on an EXTERNAL change (toolbar restyle / undo / AI),
// detected by comparing the incoming runs to what we last serialized.

function styleSpan(
  span: HTMLSpanElement,
  run: TextRun,
  layer: TextLayer,
  dims: { w: number; h: number },
  scale: number
) {
  const fam = resolveFontFamily(run.fontFamily ?? layer.fontFamily, run.text || 'x')
  const sizePx = (run.fontSizePct ?? layer.fontSizePct) * dims.h * scale
  span.style.fontFamily = `"${fam}"`
  span.style.fontWeight = String(run.fontWeight ?? layer.fontWeight)
  span.style.fontSize = `${sizePx}px`
  span.style.color = run.color ?? layer.color
  span.style.whiteSpace = 'pre-wrap'
  if (run.color != null) span.dataset.c = run.color
  if (run.fontSizePct != null) span.dataset.fs = String(run.fontSizePct)
  if (run.fontWeight != null) span.dataset.fw = String(run.fontWeight)
  if (run.fontFamily != null) span.dataset.ff = run.fontFamily
  // Typography treatments: WYSIWYG while editing + data-* markers so serializeRuns reads
  // them back — without BOTH, entering the inline editor stripped every treatment on blur
  // (silent data loss). The encircle loop draws only on the canvas (an absolute <img>
  // inside a contentEditable span would fight the caret); its pen still round-trips.
  span.style.fontSynthesis = 'none'
  if (run.italic) {
    span.style.fontStyle = 'italic'
    span.dataset.it = '1'
  }
  if (run.underlineColor != null) {
    span.style.borderBottom = `${Math.max(1, Math.round(sizePx * 0.07))}px solid ${run.underlineColor}`
    span.dataset.uc = run.underlineColor
  }
  if (run.highlightColor != null) {
    span.style.background = run.highlightColor
    span.style.padding = `${Math.round(sizePx * 0.02)}px ${Math.round(sizePx * 0.08)}px`
    span.dataset.hc = run.highlightColor
  }
  if (run.encircleColor != null) span.dataset.ec = run.encircleColor
  span.textContent = run.text
}

function lineNodes(
  layer: TextLayer,
  dims: { w: number; h: number },
  scale: number
): HTMLDivElement[] {
  return groupRunsIntoLines(layer.runs).map(line => {
    const div = document.createElement('div')
    const hasText = line.some(r => r.text.length > 0)
    if (!hasText) {
      div.appendChild(document.createElement('br'))
      return div
    }
    for (const run of line) {
      const span = document.createElement('span')
      styleSpan(span, run, layer, dims, scale)
      div.appendChild(span)
    }
    return div
  })
}

// serializeRuns (DOM → TextRuns, tolerant of browser-rewritten shapes like Cmd+A + retype's
// synthesized <font>/<b> wrappers) lives in ./inline-text-serialize so it's unit-testable.

// Map a DOM (node, offset) to a GLOBAL char offset (lines joined, '\n' between them) — the same
// addressing restyleRange uses, so a selection maps straight to a run range.
function globalOffsetOf(root: HTMLElement, node: Node | null, offset: number): number {
  if (!node) return 0
  let total = 0
  const lines = Array.from(root.children)
  for (const line of lines) {
    if (line === node || line.contains(node)) {
      const walker = document.createTreeWalker(line, NodeFilter.SHOW_TEXT)
      let n: Node | null
      while ((n = walker.nextNode())) {
        if (n === node) return total + offset
        total += (n as Text).data.length
      }
      return total + (node.nodeType === Node.TEXT_NODE ? offset : 0)
    }
    total += (line.textContent?.length ?? 0) + 1 // +1 for the '\n' between lines
  }
  return total
}

function restoreSelection(root: HTMLElement, start: number, end: number) {
  const at = (target: number): { node: Node; offset: number } => {
    let remaining = target
    const lines = Array.from(root.children)
    for (const line of lines) {
      const len = line.textContent?.length ?? 0
      if (remaining <= len) {
        const walker = document.createTreeWalker(line, NodeFilter.SHOW_TEXT)
        let n: Node | null
        while ((n = walker.nextNode())) {
          if (remaining <= (n as Text).data.length) return { node: n, offset: remaining }
          remaining -= (n as Text).data.length
        }
        return { node: line, offset: 0 }
      }
      remaining -= len + 1
    }
    const last = lines[lines.length - 1]
    return { node: last ?? root, offset: 0 }
  }
  const sel = window.getSelection()
  if (!sel) return
  const a = at(start)
  const b = at(end)
  const range = document.createRange()
  try {
    range.setStart(a.node, a.offset)
    range.setEnd(b.node, b.offset)
    sel.removeAllRanges()
    sel.addRange(range)
  } catch {
    placeCaretEnd(root)
  }
}

/** On-canvas RICH text editing: a contentEditable of styled run-spans over the (suppressed) text
 *  layer. Select any span → the floating toolbar restyles just it (see use-text-range-selection +
 *  FloatingToolbar). Typing is native + serialized to runs on input (per-span emphasis preserved);
 *  re-render only on an external change. Escape / blur exits. */
function InlineTextEditor({
  layer,
  dims,
  scale,
  editor,
  onDone,
}: {
  layer: TextLayer
  dims: { w: number; h: number }
  scale: number
  editor: CompositionEditor
  onDone: () => void
}) {
  const ref = useRef<HTMLDivElement>(null)
  const lastSerialized = useRef<string>('')
  // Rebuild-on-geometry tracking: the span font sizes are baked in px from dims.h*scale, so a
  // scale/width change mid-edit (e.g. opening the Layers dock) must rebuild even when runs are
  // unchanged — otherwise the edited text renders at a stale size vs the layer it overlays.
  const lastScale = useRef(scale)
  const lastH = useRef(dims.h)
  const editorRef = useRef(editor)
  editorRef.current = editor
  const onDoneRef = useRef(onDone)
  onDoneRef.current = onDone
  // The runs at edit-open, so Escape can revert. Captured once (the component is keyed per layer).
  const initialRuns = useRef(layer.runs)
  const reverting = useRef(false)
  const setRange = useTextRangeSelection(s => s.set)
  const clearRange = useTextRangeSelection(s => s.clear)

  // Debounced commit of the serialized runs — coarse undo steps, not one per keystroke.
  const commit = useMemo(
    () =>
      debounce((runs: TextRun[]) => {
        lastSerialized.current = JSON.stringify(runs)
        editorRef.current.setTextRuns(layer.id, runs)
      }, 250),
    [layer.id]
  )

  // Mount: build the styled-span DOM, focus, caret to end, seed the serialized + geometry baseline.
  useEffect(() => {
    const el = ref.current
    if (!el) return
    el.replaceChildren(...lineNodes(layer, dims, scale))
    lastSerialized.current = JSON.stringify(layer.runs)
    lastScale.current = scale
    lastH.current = dims.h
    el.focus()
    placeCaretEnd(el)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Rebuild + restore the selection on an EXTERNAL runs change (toolbar restyle / undo / AI revise)
  // OR a geometry change (scale/width). Our own input echoes back equal to lastSerialized at the
  // same geometry → skipped, so typing never resets the caret.
  useEffect(() => {
    const json = JSON.stringify(layer.runs)
    const geomChanged = scale !== lastScale.current || dims.h !== lastH.current
    if (json === lastSerialized.current && !geomChanged) return
    const el = ref.current
    if (!el) return
    const r = useTextRangeSelection.getState().range
    el.replaceChildren(...lineNodes(layer, dims, scale))
    lastSerialized.current = json
    lastScale.current = scale
    lastH.current = dims.h
    if (r && r.layerId === layer.id) restoreSelection(el, r.start, r.end)
  }, [layer, dims, scale])

  // Track the character selection so the toolbar can restyle just the selected span.
  useEffect(() => {
    const handler = () => {
      const el = ref.current
      if (!el) return
      const sel = window.getSelection()
      if (!sel || sel.rangeCount === 0 || !sel.anchorNode || !sel.focusNode) return
      if (!el.contains(sel.anchorNode) || !el.contains(sel.focusNode)) return
      let s = globalOffsetOf(el, sel.anchorNode, sel.anchorOffset)
      let e = globalOffsetOf(el, sel.focusNode, sel.focusOffset)
      if (s > e) [s, e] = [e, s]
      setRange({ layerId: layer.id, start: s, end: e })
    }
    document.addEventListener('selectionchange', handler)
    return () => document.removeEventListener('selectionchange', handler)
  }, [layer.id, setRange])

  // Unmount FLUSHES the pending edit (don't cancel). Editing can end via unmount without a DOM
  // blur — selecting another layer, an AI revise, a programmatic endTextEdit — and React doesn't
  // fire onBlur on unmount, so cancelling would silently drop the last keystrokes.
  useEffect(() => () => commit.flush(), [commit])

  const align = layer.align === 'center' ? 'center' : layer.align === 'right' ? 'right' : 'left'
  const ls = layer.letterSpacingPct
    ? layer.letterSpacingPct * layer.fontSizePct * dims.h * scale
    : undefined

  return (
    <div
      ref={ref}
      contentEditable
      suppressContentEditableWarning
      data-inline-text-editor={layer.id}
      onPointerDown={e => e.stopPropagation()}
      onInput={() => {
        const el = ref.current
        if (el) commit(serializeRuns(el))
      }}
      onBlur={() => {
        commit.cancel()
        if (!reverting.current) {
          const el = ref.current
          if (el) {
            const runs = serializeRuns(el)
            lastSerialized.current = JSON.stringify(runs)
            editorRef.current.setTextRuns(layer.id, runs)
          }
        }
        reverting.current = false
        clearRange()
        onDoneRef.current()
      }}
      onKeyDown={e => {
        e.stopPropagation()
        if (e.key === 'Escape') {
          e.preventDefault()
          // Revert to the text as it was when editing opened, then exit (blur skips its commit).
          commit.cancel()
          reverting.current = true
          editorRef.current.setTextRuns(layer.id, initialRuns.current)
          ;(e.currentTarget as HTMLElement).blur()
        }
      }}
      style={{
        position: 'absolute',
        left: layer.x * dims.w * scale,
        top: layer.y * dims.h * scale,
        width: layer.w * dims.w * scale,
        transform: layer.rotation ? `rotate(${layer.rotation}deg)` : undefined,
        transformOrigin: 'top left',
        lineHeight: layer.lineHeight,
        letterSpacing: ls,
        textAlign: align,
        textTransform: layer.uppercase ? 'uppercase' : undefined,
        whiteSpace: 'pre-wrap',
        outline: '1.5px solid var(--acid, #c6ff3d)',
        outlineOffset: 2,
        background: 'rgba(0,0,0,0.04)',
        caretColor: 'var(--acid, #c6ff3d)',
        cursor: 'text',
        pointerEvents: 'auto',
        zIndex: 25,
      }}
    />
  )
}
